/**
 * InteractiveOnboarding.tsx — Tour interattivo post-login con spotlight/tooltip.
 *
 * Mostra un overlay step-by-step che evidenzia le sezioni chiave dell'app:
 * Dashboard, Registrazione, Social, Classifica, Eventi, Profilo.
 *
 * Si attiva al primo login dopo la registrazione.
 * Persiste completamento in localStorage 'rc-onboarding-tour-done'.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Map, Users, Trophy, Calendar, User, ChevronRight,
  X, Circle, Zap, Activity, Star,
} from 'lucide-react';

interface TourStep {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  /** Posizione del tooltip: top, bottom, center */
  position: 'center' | 'top' | 'bottom';
  /** Route da navigare (opzionale, per deep-link) */
  route?: string;
  /** Emoji decorativa */
  emoji: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'dashboard',
    icon: <Home size={28} className="text-brand-400" />,
    title: 'Dashboard',
    description: 'Qui trovi il riepilogo delle tue statistiche: km percorsi, streak, badge guadagnati e azioni rapide. È la tua home personalizzata.',
    position: 'center',
    route: '/dashboard',
    emoji: '🏠',
  },
  {
    id: 'record',
    icon: <Map size={28} className="text-green-400" />,
    title: 'Registra Percorso',
    description: 'Tocca "Registra" per tracciare un nuovo percorso in moto. Puoi vedere la mappa in tempo reale, velocità e distanza. Al termine, ricevi un riepilogo con statistiche e badge.',
    position: 'top',
    route: '/routes/record',
    emoji: '🗺️',
  },
  {
    id: 'social',
    icon: <Users size={28} className="text-blue-400" />,
    title: 'Feed Social',
    description: 'Condividi i tuoi percorsi, commenta quelli degli amici, ricevi notifiche quando qualcuno interagisce con i tuoi post. La community ti segue!',
    position: 'center',
    route: '/social',
    emoji: '👥',
  },
  {
    id: 'leaderboard',
    icon: <Trophy size={28} className="text-amber-400" />,
    title: 'Classifica',
    description: 'Scala la classifica accumulando km! Confrontati con gli altri rider, supera i tuoi amici e raggiungi il podio. Ogni km conta.',
    position: 'center',
    route: '/social/leaderboard',
    emoji: '🏆',
  },
  {
    id: 'events',
    icon: <Calendar size={28} className="text-purple-400" />,
    title: 'Eventi & Chapter',
    description: 'Scopri eventi motociclistici vicino a te, unisciti ai chapter, partecipa ai raduni. Organizza il tuo evento e invita la community.',
    position: 'center',
    route: '/events',
    emoji: '📅',
  },
  {
    id: 'profile',
    icon: <User size={28} className="text-cyan-400" />,
    title: 'Il Tuo Profilo',
    description: 'Personalizza il tuo profilo, gestisci le impostazioni di privacy, attiva l\'autenticazione a due fattori e scegli il tema che preferisci.',
    position: 'center',
    route: '/profile',
    emoji: '👤',
  },
];

interface InteractiveOnboardingProps {
  open: boolean;
  onClose: () => void;
}

export default function InteractiveOnboarding({ open, onClose }: InteractiveOnboardingProps) {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const navigate = useNavigate();

  const handleSkip = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      localStorage.setItem('rc-onboarding-tour-done', '1');
      onClose();
      setExiting(false);
    }, 300);
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      handleSkip(); // Ultimo step → completa
    }
  }, [step, handleSkip]);

  const handlePrev = useCallback(() => {
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  const handleNavigate = useCallback((route?: string) => {
    if (route) {
      onClose();
      localStorage.setItem('rc-onboarding-tour-done', '1');
      navigate(route);
    }
  }, [navigate, onClose]);

  if (!open) return null;

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  // Posizionamento tooltip
  const positionClasses = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-16',
    bottom: 'items-end justify-center pb-24',
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] transition-opacity duration-300 ${
          exiting ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleSkip}
      />

      {/* Content */}
      <div
        className={`fixed inset-0 z-[10000] flex ${positionClasses[current.position]} p-4 pointer-events-none transition-opacity duration-300 ${
          exiting ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div
          className="pointer-events-auto bg-neutral-900/95 backdrop-blur-2xl border border-white/15 rounded-3xl max-w-sm w-full shadow-[0_20px_60px_rgba(0,0,0,0.9)] overflow-hidden"
        >
          {/* Progress bar */}
          <div className="h-1 bg-neutral-800">
            <div
              className="h-full bg-gradient-to-r from-brand-500 to-brand-400 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step counter */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <span className="text-xs text-neutral-500 font-mono">
              {step + 1} / {TOUR_STEPS.length}
            </span>
            <button
              onClick={handleSkip}
              className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              Salta tour
            </button>
          </div>

          {/* Icon + Title */}
          <div className="px-5 pb-3 text-center">
            <div className="mx-auto mb-3 w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
              {current.icon}
            </div>
            <h2 className="text-xl font-extrabold text-white mb-1">
              {current.emoji} {current.title}
            </h2>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-xs mx-auto">
              {current.description}
            </p>
          </div>

          {/* Action hint */}
          {current.route && (
            <div className="px-5 pb-3">
              <button
                onClick={() => handleNavigate(current.route)}
                className="w-full py-2.5 rounded-xl bg-brand-500/10 border border-brand-500/20 text-brand-400 text-sm font-semibold hover:bg-brand-500/20 transition flex items-center justify-center gap-2"
              >
                <span>{current.emoji}</span>
                Vai a {current.title}
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className="px-5 pb-5 flex gap-2">
            <button
              onClick={handlePrev}
              disabled={step === 0}
              className={`px-4 py-3 rounded-xl text-sm font-bold transition ${
                step === 0
                  ? 'bg-neutral-800/50 text-neutral-600 cursor-not-allowed'
                  : 'bg-neutral-800 text-white hover:bg-neutral-700'
              }`}
            >
              ← Indietro
            </button>
            <button
              onClick={handleNext}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-500 text-white font-bold text-sm hover:bg-brand-600 transition shadow-[0_4px_16px_rgba(255,107,53,0.3)]"
            >
              {isLast ? (
                <>
                  <Star size={16} />
                  Iniziamo!
                </>
              ) : (
                <>
                  Avanti
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5 pb-4">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === step
                    ? 'bg-brand-500 w-5 h-1.5'
                    : i < step
                    ? 'bg-brand-500/50 w-1.5 h-1.5'
                    : 'bg-neutral-700 w-1.5 h-1.5 hover:bg-neutral-500'
                }`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Close button (absolute, top right) */}
      <button
        onClick={handleSkip}
        className={`fixed top-4 right-4 z-[10001] p-2 rounded-full bg-neutral-800/80 text-neutral-400 hover:text-white hover:bg-neutral-700 transition pointer-events-auto ${
          exiting ? 'opacity-0' : 'opacity-100'
        }`}
        aria-label="Chiudi tour"
      >
        <X size={20} />
      </button>
    </>
  );
}
